import { z } from "zod";
import { agentAttachmentSchema } from "./attachments.js";
import { agentActorSchema } from "./messages.js";
import { agentCardSchema } from "./cards.js";
import { isoTimestampSchema, runIdSchema, threadIdSchema, } from "./identifiers.js";
import { agentMessagePresentationSchema } from "./messages.js";
import { agentTaskListSchema } from "./task-lists.js";
const uiIdSchema = z.string().trim().min(1).max(500);
// A single long-lived root can accumulate more activity than the browser
// should retain in one AI SDK message. The protected trace and canonical
// PostgreSQL records remain complete; snapshot and live projections retain the
// same latest window and never reject an otherwise valid conversation.
export const MAX_OPEN_CLOUD_UI_PARTS_PER_MESSAGE = 1_024;
export const MAX_OPEN_CLOUD_UI_TEXT_LENGTH = 200_000;
/**
 * Keeps browser-visible text within the shared UTF-16 code-unit bound used by
 * JavaScript and Zod. When the boundary falls between a surrogate pair, the
 * split high surrogate becomes a one-unit replacement marker. Reaching the
 * exact bound makes later appends no-ops instead of skipping the omitted code
 * point and resuming with later text.
 */
export function truncateOpenCloudUIText(value) {
    if (value.length <= MAX_OPEN_CLOUD_UI_TEXT_LENGTH)
        return value;
    let end = MAX_OPEN_CLOUD_UI_TEXT_LENGTH;
    const finalCodeUnit = value.charCodeAt(end - 1);
    const nextCodeUnit = value.charCodeAt(end);
    if (finalCodeUnit >= 0xd800 &&
        finalCodeUnit <= 0xdbff &&
        nextCodeUnit >= 0xdc00 &&
        nextCodeUnit <= 0xdfff) {
        return `${value.slice(0, end - 1)}\uFFFD`;
    }
    return value.slice(0, end);
}
/** Applies the same deterministic bound while coalescing repeated SSE text. */
export function appendOpenCloudUIText(current, delta) {
    if (current.length >= MAX_OPEN_CLOUD_UI_TEXT_LENGTH) {
        return truncateOpenCloudUIText(current);
    }
    return truncateOpenCloudUIText(current + delta);
}
export const openCloudUITextPartSchema = z
    .object({
    type: z.literal("text"),
    text: z.string().max(MAX_OPEN_CLOUD_UI_TEXT_LENGTH),
    state: z.enum(["streaming", "done"]).optional(),
})
    .strict();
export const openCloudUIReasoningPartSchema = z
    .object({
    type: z.literal("reasoning"),
    id: uiIdSchema,
    text: z.string().max(MAX_OPEN_CLOUD_UI_TEXT_LENGTH),
    state: z.enum(["streaming", "done"]).optional(),
})
    .strict();
export const openCloudUIToolMetadataSchema = z
    .object({
    schemaVersion: z.literal(1),
    runId: runIdSchema,
    kind: z.enum(["command", "tool"]),
    status: z.enum(["running", "succeeded", "failed", "cancelled"]),
    durationMs: z.number().int().nonnegative().nullable(),
    // A completion-only SSE patch cannot reconstruct the canonical start
    // timestamp. Null tells the client to preserve an earlier start patch and
    // wait for snapshot replay rather than fabricating one.
    startedAt: isoTimestampSchema.nullable(),
    finishedAt: isoTimestampSchema.nullable(),
    sequence: z.number().int().nonnegative(),
})
    .strict();
const dynamicToolBase = {
    type: z.literal("dynamic-tool"),
    toolName: z.string().trim().min(1).max(200),
    toolCallId: uiIdSchema,
    title: z.string().trim().min(1).max(1_000),
    toolMetadata: openCloudUIToolMetadataSchema,
    providerExecuted: z.literal(true),
};
export const openCloudUIDynamicToolPartSchema = z.union([
    z
        .object({
        ...dynamicToolBase,
        state: z.literal("input-available"),
        input: z.string().max(32_000).nullable(),
    })
        .strict(),
    z
        .object({
        ...dynamicToolBase,
        state: z.literal("output-available"),
        input: z.string().max(32_000).nullable(),
        output: z.string().max(64_000).nullable(),
    })
        .strict(),
    z
        .object({
        ...dynamicToolBase,
        state: z.literal("output-error"),
        input: z.string().max(32_000).nullable(),
        errorText: z.string().trim().min(1).max(500),
    })
        .strict(),
]);
const questionOptionSchema = z
    .object({
    id: z.string().trim().min(1).max(100),
    label: z.string().trim().min(1).max(200),
    detail: z.string().trim().min(1).max(1_000),
    response: z.string().trim().min(1).max(5_000),
    tone: z.enum(["default", "primary", "destructive", "decline"]),
})
    .strict();
const releaseReviewSchema = z
    .object({
    area: z.enum(["build", "behavior", "experience"]),
    label: z.string().trim().min(1).max(200),
    status: z.enum(["passed", "needs_work", "not_needed"]),
    detail: z.string().trim().min(1).max(2_000),
})
    .strict();
const releaseGateSchema = z
    .object({
    id: uiIdSchema,
    label: z.string().trim().min(1).max(200),
    status: z.enum(["passed", "failed", "not_applicable", "not_tested"]),
    detail: z.string().trim().min(1).max(2_000),
})
    .strict();
export const openCloudUIReleaseEvidenceSchema = z
    .object({
    deploymentId: z.uuid().nullable(),
    status: z.enum(["passed", "failed"]),
    reviews: z.array(releaseReviewSchema).max(10),
    gates: z.array(releaseGateSchema).max(60),
})
    .strict();
export const openCloudUIPlanPartSchema = z
    .object({
    type: z.literal("data-plan"),
    id: uiIdSchema,
    data: z
        .object({
        runId: runIdSchema,
        occurredAt: isoTimestampSchema,
        updatedAt: isoTimestampSchema,
        state: z.enum(["active", "done"]),
        taskList: agentTaskListSchema,
    })
        .strict(),
})
    .strict();
export const openCloudUIQuestionPartSchema = z
    .object({
    type: z.literal("data-question"),
    id: uiIdSchema,
    data: z
        .object({
        questionId: z.uuid(),
        runId: runIdSchema,
        kind: z.enum(["question", "approval"]),
        state: z.enum(["approval-required", "closed"]),
        question: z.string().trim().min(1).max(5_000),
        reason: z.enum(["material_decision", "new_authority", "policy"]),
        context: z.string().trim().min(1).max(5_000).nullable(),
        options: z.array(questionOptionSchema).max(5),
        occurredAt: isoTimestampSchema,
    })
        .strict(),
})
    .strict();
export const openCloudUIChildPartSchema = z
    .object({
    type: z.literal("data-child"),
    id: uiIdSchema,
    data: z
        .object({
        runId: runIdSchema,
        parentRunId: runIdSchema,
        purpose: z.string().trim().min(1).max(500).nullable(),
        state: z.enum([
            "queued",
            "active",
            "waiting",
            "succeeded",
            "failed",
            "cancelled",
        ]),
        outcome: z.enum(["succeeded", "failed", "cancelled"]).nullable(),
        occurredAt: isoTimestampSchema,
        updatedAt: isoTimestampSchema,
    })
        .strict(),
})
    .strict();
const cardDataFields = {
    runId: runIdSchema,
    occurredAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
    card: agentCardSchema,
};
export const openCloudUICardPartSchema = z
    .object({
    type: z.literal("data-card"),
    id: uiIdSchema,
    data: z.object(cardDataFields).strict(),
})
    .strict();
export const openCloudUIReleasePartSchema = z
    .object({
    type: z.literal("data-release"),
    id: uiIdSchema,
    data: z.object(cardDataFields).strict(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.data.card.type !== "release_card") {
        context.addIssue({
            code: "custom",
            path: ["data", "card", "type"],
            message: "A release part requires a release card",
        });
    }
});
export const openCloudUIIncidentPartSchema = z
    .object({
    type: z.literal("data-incident"),
    id: uiIdSchema,
    data: z
        .object({
        incidentId: uiIdSchema,
        source: z.enum([
            "health",
            "verification",
            "runtime",
            "function",
            "job",
            "agent_feed",
            "user_report",
        ]),
        kind: z.string().trim().min(1).max(100),
        severity: z.enum(["info", "warning", "error", "critical"]),
        title: z.string().trim().min(1).max(200),
        summary: z.string().trim().min(1).max(2_000),
        status: z.enum([
            "detected",
            "queued",
            "running",
            "awaiting_user",
            "suppressed",
            "repaired",
            "failed",
            "cancelled",
        ]),
        fireState: z.enum(["unresolved", "in_progress", "resolved"]),
        occurrenceCount: z.number().int().positive(),
        observedAt: isoTimestampSchema,
        lastObservedAt: isoTimestampSchema,
        threadId: threadIdSchema.nullable(),
        runId: runIdSchema.nullable(),
    })
        .strict(),
})
    .strict();
export const openCloudUITerminalPartSchema = z
    .object({
    type: z.literal("data-terminal"),
    id: uiIdSchema,
    data: z
        .object({
        runId: runIdSchema,
        status: z.enum(["released", "completed", "failed", "cancelled"]),
        summary: z.string().trim().min(1).max(5_000),
        occurredAt: isoTimestampSchema,
        release: openCloudUIReleaseEvidenceSchema.nullable(),
    })
        .strict(),
})
    .strict();
export const openCloudUIMessagePartSchema = z.union([
    z.object({ type: z.literal("data-attachment"), id: uiIdSchema,
        data: agentAttachmentSchema.extend({ occurredAt: isoTimestampSchema }) }).strict(),
    openCloudUITextPartSchema,
    openCloudUIReasoningPartSchema,
    openCloudUIDynamicToolPartSchema,
    openCloudUIPlanPartSchema,
    openCloudUIQuestionPartSchema,
    openCloudUIChildPartSchema,
    openCloudUICardPartSchema,
    openCloudUIReleasePartSchema,
    openCloudUIIncidentPartSchema,
    openCloudUITerminalPartSchema,
]);
export const openCloudUIMessageMetadataSchema = z
    .object({
    schemaVersion: z.literal(1),
    actor: agentActorSchema.optional(),
    threadId: threadIdSchema,
    runId: runIdSchema.nullable(),
    source: z.enum(["transcript", "run"]),
    sourceMessageId: z.uuid().optional(),
    createdAt: isoTimestampSchema,
    sequence: z.number().int().nonnegative().nullable(),
    presentation: agentMessagePresentationSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.source === "transcript") {
        if (!value.sourceMessageId || value.sequence === null) {
            context.addIssue({
                code: "custom",
                message: "A transcript UI message requires its durable source identity",
            });
        }
        return;
    }
    if (value.sourceMessageId !== undefined ||
        value.sequence !== null ||
        value.presentation !== undefined ||
        value.runId === null) {
        context.addIssue({
            code: "custom",
            message: "A runtime-owned UI message must use only its run identity",
        });
    }
});
export const openCloudUIMessageSchema = z
    .object({
    id: uiIdSchema,
    role: z.enum(["system", "user", "assistant"]),
    metadata: openCloudUIMessageMetadataSchema,
    parts: z
        .array(openCloudUIMessagePartSchema)
        .min(1)
        .max(MAX_OPEN_CLOUD_UI_PARTS_PER_MESSAGE),
})
    .strict()
    .superRefine((value, context) => {
    if (value.metadata.source === "transcript" &&
        value.id !== value.metadata.sourceMessageId) {
        context.addIssue({
            code: "custom",
            path: ["id"],
            message: "A transcript UI message keeps its canonical message ID",
        });
    }
    if (value.metadata.source === "run" && value.role !== "assistant") {
        context.addIssue({
            code: "custom",
            path: ["role"],
            message: "Run activity belongs to an assistant UI message",
        });
    }
});
const streamMetadataSchema = z
    .object({
    schemaVersion: z.literal(1),
    threadId: threadIdSchema,
    runId: runIdSchema,
    occurredAt: isoTimestampSchema,
    sequence: z.number().int().nonnegative(),
    messageCreatedAt: isoTimestampSchema.nullable(),
})
    .strict();
export const openCloudUIMessagePatchSchema = z
    .object({
    schemaVersion: z.literal(1),
    messageId: uiIdSchema,
    role: z.literal("assistant"),
    operation: z.enum(["append-text", "upsert-part"]),
    metadata: streamMetadataSchema,
    part: openCloudUIMessagePartSchema,
})
    .strict()
    .superRefine((value, context) => {
    if ((value.operation === "append-text") !== (value.part.type === "text")) {
        context.addIssue({
            code: "custom",
            path: ["operation"],
            message: "Only a text part can be appended",
        });
    }
    if (value.operation === "append-text" &&
        value.part.type === "text" &&
        value.part.state !== "streaming") {
        context.addIssue({
            code: "custom",
            path: ["part", "state"],
            message: "An appended text part must be streaming",
        });
    }
});
/**
 * Orders browser-visible activity by the same durable coordinates used by a
 * snapshot, then retains its latest shared window. Live arrival order is not a
 * replay coordinate: callbacks and completion updates can arrive after an
 * older identity has already left the window.
 */
export function boundOpenCloudUIMessageParts(parts) {
    return [...parts]
        .sort(compareOpenCloudUIMessageParts)
        .slice(-MAX_OPEN_CLOUD_UI_PARTS_PER_MESSAGE);
}
export function compareOpenCloudUIMessageParts(left, right) {
    const leftOrder = openCloudUIMessagePartOrder(left);
    const rightOrder = openCloudUIMessagePartOrder(right);
    return (leftOrder.occurredAt.localeCompare(rightOrder.occurredAt) ||
        leftOrder.sequence - rightOrder.sequence ||
        leftOrder.identity.localeCompare(rightOrder.identity));
}
function openCloudUIMessagePartOrder(part) {
    if (part.type === "text") {
        return { occurredAt: "", sequence: 0, identity: "text" };
    }
    if (part.type === "reasoning") {
        return { occurredAt: "", sequence: 1, identity: `reasoning:${part.id}` };
    }
    if (part.type === "dynamic-tool") {
        return {
            occurredAt: part.toolMetadata.startedAt ?? part.toolMetadata.finishedAt ?? "",
            sequence: part.toolMetadata.sequence,
            identity: `tool:${part.toolCallId}`,
        };
    }
    if (part.type === "data-plan") {
        return {
            occurredAt: part.data.occurredAt,
            sequence: Number.MAX_SAFE_INTEGER - 50,
            identity: part.id,
        };
    }
    if (part.type === "data-question") {
        return {
            occurredAt: part.data.occurredAt,
            sequence: Number.MAX_SAFE_INTEGER - 40,
            identity: part.id,
        };
    }
    if (part.type === "data-child") {
        return {
            occurredAt: part.data.occurredAt,
            sequence: Number.MAX_SAFE_INTEGER - 30,
            identity: part.id,
        };
    }
    if (part.type === "data-card" || part.type === "data-release" || part.type === "data-attachment") {
        return {
            occurredAt: part.data.occurredAt,
            sequence: Number.MAX_SAFE_INTEGER - 20,
            identity: part.id,
        };
    }
    if (part.type === "data-incident") {
        return {
            occurredAt: part.data.observedAt,
            sequence: Number.MAX_SAFE_INTEGER - 15,
            identity: part.id,
        };
    }
    return {
        occurredAt: part.data.occurredAt,
        sequence: Number.MAX_SAFE_INTEGER - 10,
        identity: part.id,
    };
}
//# sourceMappingURL=ui-messages.js.map